import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint, yieldToMainThread } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {Promise<void> | null} */
  #pendingCartUpdate = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Returns the section ID for the cart-items-component inside this drawer.
   * @returns {string | null}
   */
  #getCartSectionId() {
    const cartItemsComponent = this.querySelector('cart-items-component');
    return cartItemsComponent?.dataset?.sectionId ?? null;
  }

  /**
   * Ensures the drawer content is up-to-date by morphing the section
   * with fresh HTML from the server if sections were provided in the event,
   * or by fetching fresh content via the Section Rendering API.
   *
   * @param {Record<string, string> | undefined} sections - Sections HTML from the cart event.
   * @returns {Promise<void>}
   */
  async #ensureContentUpdated(sections) {
    const sectionId = this.#getCartSectionId();
    if (!sectionId) return;

    if (sections?.[sectionId]) {
      // We already have the fresh HTML from the cart add response — morph immediately
      morphSection(sectionId, sections[sectionId], { mode: 'hydration' });
    } else {
      // No pre-fetched HTML available — fetch from Section Rendering API
      await sectionRenderer.renderSection(sectionId, { cache: false, mode: 'hydration' });
    }

    // After morphing, ensure all child components refresh their refs.
    // This is critical for the empty→populated transition where the
    // MutationObserver may not have been installed yet (requestIdleCallback delay)
    // and onAfterUpdate only fires on the morphed <div> node, not on Component instances.
    this.querySelectorAll('cart-items-component, cart-quantity-selector-component, quantity-selector-component').forEach((component) => {
      if (typeof /** @type {any} */ (component).updatedCallback === 'function') {
        /** @type {any} */ (component).updatedCallback();
      }
    });
  }

  /**
   * Handles cart add events - updates content, opens drawer if auto-open, and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { sections?: Record<string, string> } }>} event
   */
  #handleCartAdd = async (event) => {
    const sections = event.detail?.data?.sections;

    // Start updating the drawer content immediately
    this.#pendingCartUpdate = this.#ensureContentUpdated(sections);

    if (this.hasAttribute('auto-open')) {
      // Wait for content to be updated before opening
      try {
        await this.#pendingCartUpdate;
      } catch (e) {
        // Even if update fails, still open the drawer
      }
      this.#pendingCartUpdate = null;

      // Remove the empty class from the dialog if the cart now has items
      const { dialog } = this.refs;
      if (dialog) {
        dialog.classList.remove('cart-drawer--empty');
      }

      await yieldToMainThread();

      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  async open() {
    this.setAttribute('loading', '');

    try {
      // When manually opening the cart drawer (click on cart icon),
      // always fetch fresh content to ensure we show the latest state.
      const sectionId = this.#getCartSectionId();

      if (sectionId) {
        // If there's a pending cart update from a recent add, wait for it
        if (this.#pendingCartUpdate) {
          try {
            await this.#pendingCartUpdate;
          } catch (e) {
            // Continue even if pending update fails
          }
          this.#pendingCartUpdate = null;
        } else {
          // No pending update — fetch fresh content before opening
          try {
            await sectionRenderer.renderSection(sectionId, { cache: false, mode: 'hydration' });
          } catch (e) {
            // Continue even if fetch fails — show stale content rather than nothing
          }
        }
      }

      // Update the empty class on the dialog
      const { dialog } = this.refs;
      if (dialog) {
        const hasCartForm = dialog.querySelector('.cart-form');
        if (hasCartForm) {
          dialog.classList.remove('cart-drawer--empty');
        } else {
          dialog.classList.add('cart-drawer--empty');
        }
      }

      // Yield to the main thread to let DOM morphing and class changes settle before starting the animation
      await yieldToMainThread();

      this.showDialog();
    } finally {
      this.removeAttribute('loading');
    }

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
